import React from 'react';

import {
  RadioCardGroup,
  type RadioCardOption,
} from '@/components/ui/radio-card-group';
import { CredentialType } from '@/sections/asset/components/tabs/credentials/credentialForms/types';

interface CredentialTypeSelectionProps {
  isWebService: boolean;
  selectedType: CredentialType;
  onTypeChange: (type: CredentialType) => void;
  /**
   * Optional map of credential types to disable, keyed by type with the
   * explanatory note shown beneath the gated card. Each matching option is
   * rendered non-selectable with its note; all other options are untouched.
   * Omit for the default (everything selectable) behavior.
   */
  disabledTypes?: Partial<Record<CredentialType, string>>;
}

// Web-application flow: 3 options. Automated Authentication leads (AI auto-login
// + session capture) and is the default; Use Existing Token and Manual Login
// Flow follow.
const WEB_APPLICATION_OPTIONS: RadioCardOption<CredentialType>[] = [
  {
    value: CredentialType.COMPLEX_LOGIN,
    label: 'Automated Authentication',
    description:
      "Provide a username and password — Guard's AI signs in for you and records the authenticated session, so every scan runs fully logged in.",
  },
  {
    value: CredentialType.STATIC_TOKEN,
    label: 'Use Existing Token',
    description:
      'Already have a token? Paste your auth headers (Bearer token, API key, or cookies) and Guard reuses them on every request.',
  },
  {
    value: CredentialType.RECIPE,
    label: 'Manual Login Flow',
    description:
      "Build the login sequence step by step — requests, extracted tokens, captured headers. Use when Automated Authentication can't handle the flow.",
  },
];

// Web-service flow: 4 options rooted at Basic Authentication.
const WEB_SERVICE_OPTIONS: RadioCardOption<CredentialType>[] = [
  {
    value: CredentialType.BASIC,
    label: 'Basic Authentication',
    description: 'Username and password authentication',
  },
  {
    value: CredentialType.BEARER_TOKEN,
    label: 'Bearer Token',
    description:
      'Token-based authentication with fixed or dynamic token retrieval',
  },
  {
    value: CredentialType.API_KEY,
    label: 'API Key',
    description: 'API key authentication in header, query, or cookie',
  },
  {
    value: CredentialType.STATIC_TOKEN,
    label: 'Use Existing Token',
    description:
      'Already have a token? Paste your auth headers (Bearer token, API key, or cookies) and Guard reuses them on every request.',
  },
];

export const CredentialTypeSelection: React.FC<
  CredentialTypeSelectionProps
> = ({ isWebService, selectedType, onTypeChange, disabledTypes }) => {
  const baseOptions = isWebService
    ? WEB_SERVICE_OPTIONS
    : WEB_APPLICATION_OPTIONS;
  const options = disabledTypes
    ? baseOptions.map(option =>
        option.value in disabledTypes
          ? {
              ...option,
              disabled: true,
              disabledNote: disabledTypes[option.value],
            }
          : option
      )
    : baseOptions;

  return (
    <div className="pt-1">
      <RadioCardGroup
        value={selectedType}
        onChange={onTypeChange}
        options={options}
        ariaLabel="Authentication method"
      />
    </div>
  );
};
